import React, { useState, useEffect } from 'react'
import { XMarkIcon, EyeIcon, EyeSlashIcon, KeyIcon } from '@heroicons/react/24/outline'
import axiosInstance from '../../api/axiosInstance'
import toast from 'react-hot-toast'
import { generateSecurePassword } from '../../utils/passwordGenerator'

interface UserForPasswordChange {
  id: number
  first_name: string
  last_name: string
  email: string
}

interface ChangePasswordModalProps {
  isOpen: boolean
  onClose: () => void
  user: UserForPasswordChange | null
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose, user }) => {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setPassword('')
      setConfirmPassword('')
      setShowPassword(false)
      setShowConfirmPassword(false)
      setLoading(false)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, loading, onClose])

  if (!isOpen || !user) return null

  const handleGeneratePassword = () => {
    const generatedPassword = generateSecurePassword(11, 15)
    setPassword(generatedPassword)
    setConfirmPassword(generatedPassword)
    setShowPassword(true)
    setShowConfirmPassword(true)
    toast.success('Contraseña segura generada', { position: 'top-center' })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!password || password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres', { position: 'top-center' })
      return
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden', { position: 'top-center' })
      return
    }

    try {
      setLoading(true)
      const response = await axiosInstance.put(`/users/${user.id}/password`, {
        password
      })

      if (response.data?.success) {
        toast.success(response.data.message || 'Contraseña actualizada exitosamente', {
          position: 'top-center'
        })
        onClose()
        return
      }

      toast.error(response.data?.message || 'No fue posible actualizar la contraseña', {
        position: 'top-center'
      })
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Error al actualizar la contraseña'
      toast.error(message, { position: 'top-center' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onClose()
      }}
    >
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Cambiar contraseña</h2>
            <p className="mt-1 text-sm text-gray-500">Actualiza la contraseña del usuario seleccionado</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            aria-label="Cerrar modal de cambio de contraseña"
            disabled={loading}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
          <p className="font-medium">
            {user.first_name} {user.last_name}
          </p>
          <p>{user.email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
              Nueva contraseña
            </label>
            <div className="relative mt-1">
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="block h-10 w-full rounded-md border-gray-300 pr-20 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Mínimo 8 caracteres"
                minLength={8}
                required
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                disabled={loading}
              >
                {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="absolute inset-y-0 right-10 flex items-center px-2 text-gray-500 hover:text-blue-600"
                aria-label="Generar contraseña segura"
                title="Generar contraseña segura"
                disabled={loading}
              >
                <KeyIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
              Confirmar contraseña
            </label>
            <div className="relative mt-1">
              <input
                id="confirm-password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="block h-10 w-full rounded-md border-gray-300 pr-20 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Repite la contraseña"
                minLength={8}
                required
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                aria-label={showConfirmPassword ? 'Ocultar confirmación de contraseña' : 'Mostrar confirmación de contraseña'}
                disabled={loading}
              >
                {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
              </button>
              <button
                type="button"
                onClick={handleGeneratePassword}
                className="absolute inset-y-0 right-10 flex items-center px-2 text-gray-500 hover:text-blue-600"
                aria-label="Generar contraseña segura"
                title="Generar contraseña segura"
                disabled={loading}
              >
                <KeyIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-md border border-transparent bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Actualizando...' : 'Actualizar contraseña'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ChangePasswordModal
